import React, { useEffect } from "react";

import { View, Platform } from "react-native";

import {
  Error,
  Loading,
  TabHeader,
  Separator,
  UpdateAlert,
  ScrollLayout,
  PrimaryItemCard,
  OrganizationCard,
  SecondaryItemCard,
  Button,
} from "components";
import UserInfoCard from "./UserInfoCard";
import TodayRemindersSection from "./TodayRemindersSection";

import { useRecoilValue, useSetRecoilState } from "recoil";
import { sizes, colors, VERSION } from "assets/constants";
import { authAtom, factorStepsAtom } from "stores";
import { useChmod, useFlag } from "hooks";
import { Notify } from "services";
import { sn } from "utils";

import {
  useHome,
  useVersion,
  useFactorStatuses,
  useGetSectionsPermissions,
} from "clients/apis";
import { otherItems, homePrimaryItems, productSectionItems } from "./homeItems";
import GetAllCustomers from "./GetAllCustomers";

export default function Home() {
  const { chmod } = useChmod();
  const user = useRecoilValue(authAtom);
  const setFactorSteps = useSetRecoilState(factorStepsAtom);
  const { flg: update, on: showUpdate, off: hideUpdate } = useFlag();

  const { isLoading, data, isError, error, refetch, isRefetching } = useHome();
  const { refetch: getSectionsPermissions } = useGetSectionsPermissions();
  const { data: factorSteps } = useFactorStatuses();
  const { data: updateInfo } = useVersion();

  useEffect(() => {
    getSectionsPermissions();
  }, []);

  useEffect(() => {
    if (Platform.OS === "android") {
      if (Number(updateInfo?.data?.version) > Number(VERSION.APP)) {
        if (updateInfo?.data?.is_force === 1) {
          showUpdate();
          return;
        }

        if (
          user?.upgrade?.version === updateInfo?.data?.version &&
          !user?.upgrade?.dntShow
        ) {
          showUpdate();
          return;
        }
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateInfo]);

  useEffect(() => {
    factorSteps?.data?.statuses &&
      setFactorSteps(Object?.values(factorSteps?.data?.statuses));
  }, [factorSteps]);

  const _renderContent = () => {
    if (isLoading) {
      return <Loading style={{ marginTop: 20 }} />;
    }

    if (isError) {
      return <Error error={error} reset={refetch} loading={isRefetching} />;
    }

    return (
      <>
        {/** first row */}
        <View
          style={{
            marginBottom: sizes.space,
            flexDirection: sn("column", "column", "row-reverse", "row-reverse"),
          }}
        >
          {chmod("company", "GET") ? (
            <>
              <View
                style={{
                  height: sn(140, 140, 150, 150),
                  ...Platform.select({
                    web: { flex: sn(null, null, 1, 1) },
                    native: { flex: 1 },
                  }),
                }}
              >
                <OrganizationCard numberOfEmployee={data?.data?.count_staff} />
              </View>
              <Separator
                color={colors.transparent}
                axis={sn("x", "x", "y", "y")}
              />
            </>
          ) : !(
              chmod("attribute", "GET") &&
              chmod("category", "GET") &&
              chmod("attribute", "GET")
            ) ? null : (
            sn(false, false, true, true) && (
              <>
                <UserInfoCard />
                <Separator
                  color={colors.transparent}
                  axis={sn("x", "x", "y", "y")}
                />
              </>
            )
          )}

          {!(
            chmod("attribute", "GET") &&
            chmod("category", "GET") &&
            chmod("product", "GET")
          ) ? null : (
            <View
              style={{
                flexDirection: "row-reverse",
                height: sn(140, 140, 150, 150),
                ...(sn(false, false, true, true) && { flex: 1 }),
              }}
            >
              {productSectionItems(
                data?.data?.attribute,
                data?.data?.category,
                data?.data?.products
              ).map((item, key) => (
                <React.Fragment key={String(key)}>
                  {key !== 0 && <View style={{ width: sizes.space }} />}
                  <View style={{ flex: 1 }}>
                    <SecondaryItemCard
                      {...item}
                      disabled={!chmod(item.section, "GET")}
                      disabledAdd={!chmod(item.section, "POST")}
                    />
                  </View>
                </React.Fragment>
              ))}
            </View>
          )}
        </View>

        {/** second row */}
        <View
          style={{
            flexDirection: sn("column", "column", "row-reverse", "row-reverse"),
            marginBottom: sn(
              sizes.space,
              sizes.space,
              sizes.space / 2,
              sizes.space / 2
            ),
          }}
        >
          <View
            style={{
              ...(sn(false, false, true, true) && { flex: 2 }),
              flexDirection: sn(
                "column",
                "row-reverse",
                "row-reverse",
                "row-reverse"
              ),
            }}
          >
            {homePrimaryItems(
              {
                total: data?.data?.all_customer,
                counts: data?.data?.final_customer,
              },
              {
                total: data?.data?.factor,
                counts: data?.data?.final_factor,
              }
            ).map((item, key) => {
              if (
                !chmod(item.section, "GET") &&
                sn(true, false, false, false)
              ) {
                return null;
              }

              return (
                <React.Fragment key={String(key)}>
                  {key !== 0 && <View style={{ width: sizes.space }} />}
                  <View
                    style={{
                      flex: 1,
                      marginBottom: key === 0 ? sizes.space : 0,
                    }}
                  >
                    <PrimaryItemCard
                      iconContainerWidth={50}
                      {...item}
                      disabled={!chmod(item.section, "GET")}
                      disabledAdd={!chmod(item.section, "POST")}
                    />
                  </View>
                </React.Fragment>
              );
            })}
          </View>

          <Separator color={colors.transparent} axis={sn("x", "x", "y", "y")} />

          <View
            style={{
              ...(sn(false, false, true, true) && { flex: 1 }),
              flexDirection: "row-reverse",
              height: sn(130, 130, 150, 150),
            }}
          >
            {otherItems(data?.data?.reminders_count, data?.data?.note).map(
              (item, key) => (
                <React.Fragment key={String(key)}>
                  {key !== 0 && <View style={{ width: sizes.space }} />}
                  <View style={{ flex: 1 }}>
                    <SecondaryItemCard
                      {...item}
                      disabled={!chmod(item.section, "GET")}
                      disabledAdd={!chmod(item.section, "POST")}
                    />
                  </View>
                </React.Fragment>
              )
            )}
          </View>
        </View>
      </>
    );
  };

  return (
    <>
      <Notify />
      <TabHeader />

      <ScrollLayout>
        {_renderContent()}

        {chmod("reminder", "GET") && <TodayRemindersSection />}
      </ScrollLayout>

      {Platform.OS === "android" && (
        <UpdateAlert
          visible={update}
          hide={hideUpdate}
          updateInfo={updateInfo?.data}
        />
      )}
    </>
  );
}
